import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { chatsApi } from "@/lib/humhub-api/chats";

/**
 * Hook for searching chat rooms by keyword
 * @param initialKeyword - Initial search keyword
 * @param initialMessageIds - Initial message IDs to filter by
 */
export const useSearchChatRooms = (initialKeyword?: string, initialMessageIds?: string[]) => {
  const [keyword, setKeyword] = useState<string | undefined>(initialKeyword);
  const [messageIds, setMessageIds] = useState<string[] | undefined>(initialMessageIds);
  const queryClient = useQueryClient();

  // React Queryでチャットルーム検索
  const {
    data: response,
    isLoading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ["searchChatRooms", keyword, messageIds],
    queryFn: async () => {
      if (!keyword) {
        throw new Error("Search keyword is undefined");
      }
      const result = await chatsApi.searchChatRooms({ keyword, messageIds });
      return result;
    },
    enabled: !!keyword, // キーワードがある場合のみクエリ実行
    staleTime: 3 * 60 * 1000, // チャットルーム検索結果は3分間キャッシュ
  });

  // 検索実行関数
  const searchChatRooms = useCallback(
    async (searchKeyword = initialKeyword, searchMessageIds = initialMessageIds) => {
      if (!searchKeyword) {
        return null;
      }

      setKeyword(searchKeyword);
      setMessageIds(searchMessageIds);

      // setKeywordが完了すると、useQueryのqueryKeyが変わり自動で新しいクエリが実行される
      // 既にキャッシュされている場合は即座に結果を返す
      const cachedResult = queryClient.getQueryData([
        "searchChatRooms",
        searchKeyword,
        searchMessageIds,
      ]);
      if (cachedResult) {
        return cachedResult;
      }

      // キャッシュがない場合は新しいクエリの結果を待つ
      try {
        const result = await queryClient.fetchQuery({
          queryKey: ["searchChatRooms", searchKeyword, searchMessageIds],
          queryFn: () =>
            chatsApi.searchChatRooms({ keyword: searchKeyword, messageIds: searchMessageIds }),
          staleTime: 3 * 60 * 1000,
        });
        return result;
      } catch (error) {
        console.error("Search failed:", error);
        return null;
      }
    },
    [initialKeyword, initialMessageIds, queryClient],
  );

  // 手動リフェッチ
  const refetchSearch = useCallback(() => {
    if (keyword) {
      refetch();
    }
  }, [keyword, refetch]);

  // 検索履歴のプリフェッチ（オプション）
  const prefetchSearch = useCallback(
    (searchKeyword: string, searchMessageIds?: string[]) => {
      queryClient.prefetchQuery({
        queryKey: ["searchChatRooms", searchKeyword, searchMessageIds],
        queryFn: () =>
          chatsApi.searchChatRooms({ keyword: searchKeyword, messageIds: searchMessageIds }),
        staleTime: 3 * 60 * 1000,
      });
    },
    [queryClient],
  );

  // 検索結果をクリア
  const clearSearch = useCallback(() => {
    setKeyword(undefined);
    setMessageIds(undefined);
  }, []);

  return {
    isLoading,
    error: queryError ? String(queryError) : null,
    searchChatRooms,
    response,
    refetchSearch, // 新しく追加：手動リフェッチ
    prefetchSearch, // 新しく追加：プリフェッチ
    clearSearch, // 新しく追加：検索結果クリア
    currentKeyword: keyword, // 新しく追加：現在の検索キーワード
  };
};
